import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import useFetch from 'use-http';
import Select, { StylesConfig } from 'react-select';
import { FaChevronDown } from 'react-icons/fa';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import UserDetails from 'src/models/UserDetails';
import { UserRoles } from 'src/models/UserRole';
import selectors from 'src/selectors';
import Authorization from 'src/util/Authorization';
import { ITableConfig } from 'src/components/common/table';
import { ODataFilter, ODataOptions, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';

interface IManagerOptionType {
    value: number;
    label: string;
    tsdNo: number;
    firstName: string;
    lastName: string;
}

interface IPeriodOptionType {
    value: number;
    label: string;
}

export interface ICommissionSearchCriteria {
    periodNo: number;
    commissionType: string;
    mgrNo?: number;
    clNo?: number;
    prNo?: number;
    tsdNo?: number;
}

interface ICommissionSearchProps {
    onSearch: (criteria: ICommissionSearchCriteria) => void;
    onBuildManagerReview: (criteria: ICommissionSearchCriteria) => void;
    onBuildSingleManagerReview: (criteria: ICommissionSearchCriteria) => void;
    onAddPeriod: (criteria: ICommissionSearchCriteria) => void;
    onReports: (criteria: ICommissionSearchCriteria) => void;
    onSummaryReport: (criteria: ICommissionSearchCriteria) => void;
    onExcelReport: (criteria: ICommissionSearchCriteria) => void;
    onPcAsmTotals: (criteria: ICommissionSearchCriteria) => void;
    onBonusHistory: (criteria: ICommissionSearchCriteria) => void;
    onMultiplePeriods: () => void;
}

// COMMISSION_TYPE's own 2 static List Elements (oid 32/33), confirmed via the .fmt - value codes
// match :control.commission_type's literal LOV values ("Margin"/S, "Bus. Dev."/B).
const COMMISSION_TYPE_OPTIONS = [
    { value: 'S', label: 'Margin' },
    { value: 'B', label: 'Bus. Dev.' },
];

const ADVANCED_FIELD_NAMES = ['clNoSearch', 'prNoSearch', 'tsdNoSearch'];

// Explicit useForm() generic - a narrower inline defaultValues object (just commissionType) makes
// react-hook-form infer getValues()'s type from that literal alone, so the advanced-search fields
// (clNoSearch/prNoSearch/tsdNoSearch) and mgrNoDisplay read as TS2339 errors.
interface ICommissionSearchFormValues {
    commissionType: string;
    mgrNoDisplay: string;
    clNoSearch: string;
    prNoSearch: string;
    tsdNoSearch: string;
}

// Mirrors CONTROL block (canvas MAIN) - Period No./Commission Type stay in the main bar (matching
// KEY-NEXT-ITEM [oid 28]'s own Query_Block('Commission') firing off Period No entry); the button
// panel below the search bar covers Build/Single/Reports/Summary/Excel plus PC/ASM Totals (canvas
// PCAPM) and Bonus History (canvas HIST_CONTROL) - both initially thought dead (no Canvas in a
// stale read of the .fmt) but confirmed live once actually checked. Mult/AMPM is still dead/unused
// (Quarterly AM/PM Excel report, confirmed retired by the DBA - paid via profit sharing now). And
// Beg Date/End Date (read-only, auto-populated from period_no - not search criteria, same reasoning
// as TeNonReconSearch's own exclusion) are dropped entirely. Client No./Project No./Landman No.
// (real COMMISSION_DETAIL columns, matching the master-detail relation) live in Advanced Search.
// Search/Minimize/collapse behavior matches TeNonReconSearch's own pattern exactly.
//
// Manager moved out of Advanced Search into the main bar (2026-09-14), mirroring
// manager_commission.fmt's own Control.Tsd_No - its SET_ACCESS trigger makes that field the
// primary/only search criterion on the main canvas (never buried under an advanced toggle), and
// locks it read-only to the logged-in manager's own tsd_no unless they're one of a handful of
// exempted users. Same idea here: Admin/Accounting Admin get a searchable name+number combo (same
// pattern as PersonnelSearch.tsx/ClientProjectSearch.tsx's own manager pickers) so they can pull up
// any manager; everyone else gets a read-only display force-filled to their own TSD_NO (from the
// "lms" JWT claim), so a manager using this screen only ever sees their own commissions. A small
// VP/Area Manager badge (2026-09-14) shows next to whichever manager is currently selected/shown,
// independent of the deferred VP/AM tailored-experience work - just a visual signal for now.
// NOTE: this only distinguishes "admin-ish" vs. "everyone else" for the read/write split - VP/Area
// Manager-specific scoping of what they can search/see is still deferred.
// Mirrors PersonnelSearch.tsx's own react-select manager combo (name + number, searchable by
// either) - narrower height to match this bar's other fields. Shared (not IManagerOptionType-typed)
// so the Period No. combo below can reuse the identical sizing.
const compactSelectStyles: StylesConfig<{ value: number; label: string }, false> = {
    control: (provided) => ({ ...provided, minHeight: '3rem', height: '3rem' }),
    valueContainer: (provided) => ({ ...provided, height: '3rem', padding: '0 8px' }),
    input: (provided) => ({ ...provided, margin: '0', padding: '0' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '3rem' }),
    menu: (provided) => ({ ...provided, zIndex: 999 }),
};

const CommissionSearch = (props: ICommissionSearchProps) => {
    const currentUser = useSelector(selectors.auth.user) as UserDetails;
    const canSearchAnyManager = Authorization.IsInRole([UserRoles.Admin, UserRoles.AccountingAdmin], currentUser);
    const ownMgrNo = currentUser?.tsdNo;

    const { register, errors, setValue, getValues } = useForm<ICommissionSearchFormValues>({ defaultValues: { commissionType: 'S' } });
    const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    // Manager combo - populated from the same manager list ProjectMaintenancePage/FindManagerDialog
    // use, so both admin free-search and the read-only PM display can look up a name for a tsdNo.
    const { get } = useFetch();
    const [managers, setManagers] = useState<Array<{ tsdNo: number; firstName: string; lastName: string }>>([]);
    // VP/Area Manager/Project Manager tsdNo lookups, for the "is this manager a VP/AM/PM" badge -
    // small, rarely-changing lists (see CommissionStructure), so fetched once and matched
    // client-side rather than adding a dedicated "resolve tier for tsdNo" endpoint. PM comes from
    // the same COMMISSION_ROLE_PERCENT table as VP/AM (ROLE_TIER='pm'), not the LMS UserRole - it's
    // the commission-tree tier, independent of what login role someone holds.
    const [vpTsdNos, setVpTsdNos] = useState<Set<number>>(new Set());
    const [areaManagerTsdNos, setAreaManagerTsdNos] = useState<Set<number>>(new Set());
    const [projectManagerTsdNos, setProjectManagerTsdNos] = useState<Set<number>>(new Set());
    const [selectedMgrNo, setSelectedMgrNo] = useState<number | undefined>(canSearchAnyManager ? undefined : ownMgrNo);

    // Period No. combo - replaces the old FIND_PP popup (a plain scrollable Period No./Begin
    // Date/End Date list, oid 29's double-click LOV) with the same debounced-search Select pattern
    // ClientProjectSearch.tsx already uses for its own Pay Periods field: server-side search via
    // AccountsPayable/payperiods, typing either "202606" or "2026-06" matches (normalizePeriodInput
    // strips dashes on both sides before comparing), initial options default to periods within ~6
    // months of now (same window ProjectBillingPage's own initializePayPeriodSearchSettings uses).
    const { get: getPayPeriods } = useFetch();
    const [periodOptions, setPeriodOptions] = useState<Array<IPeriodOptionType>>([]);
    const [selectedPeriodOption, setSelectedPeriodOption] = useState<IPeriodOptionType | null>(null);
    const [isSearchingPeriods, setIsSearchingPeriods] = useState<boolean>(false);

    const insertDash = (periodNumber: number) => {
        const str = periodNumber.toString();
        const insertIndex = str.length - 2;
        return str.slice(0, insertIndex) + '-' + str.slice(insertIndex);
    };

    const normalizePeriodInput = (str: string) => str.replace(/-/g, '');

    const loadPeriodOptions = async (searchTerm: string) => {
        const tableConfig: ITableConfig = { rowCount: 100, idField: 'Id', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        options.clearFilters();
        options.addPaging(1, 50);
        options.addOrderBy('Id', ODataOrderByDirection.Descending);

        const numericTerm = Number(normalizePeriodInput(searchTerm || ''));
        if (searchTerm && !isNaN(numericTerm)) {
            options.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, numericTerm));
        } else {
            const now = new Date();
            const begDate = new Date(new Date().setMonth(now.getMonth() - 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            const endDate = new Date(new Date().setMonth(now.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            options.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThan, endDate));
            options.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThan, begDate));
        }

        const result = await getPayPeriods(`/v1.0/AccountsPayable/payperiods${options.toString()}`);
        if (Array.isArray(result?.items)) {
            setPeriodOptions(result.items.map((pp: { periodNumber: number }) => ({
                value: pp.periodNumber,
                label: insertDash(pp.periodNumber),
            })));
        }
    };

    const periodSearch = _.debounce((inputText: string) => loadPeriodOptions(inputText), 300);

    useEffect(() => {
        (async () => {
            const result = await get('/v1.0/landmen/personnel/get-managers');
            if (Array.isArray(result)) {
                setManagers(result);
            }
        })();
        (async () => {
            const result = await get('/v1.0/Payroll/commissionstructure/vp-managers');
            if (Array.isArray(result)) {
                setVpTsdNos(new Set(result.map((vp: { tsdNo: number }) => vp.tsdNo)));
            }
        })();
        (async () => {
            const result = await get('/v1.0/Payroll/commissionstructure/area-managers');
            if (Array.isArray(result)) {
                setAreaManagerTsdNos(new Set(result.map((am: { mgrNo: number }) => am.mgrNo)));
            }
        })();
        (async () => {
            const result = await get('/v1.0/Payroll/commissionstructure/role-percents');
            if (Array.isArray(result)) {
                setProjectManagerTsdNos(new Set(
                    result.filter((rp: { roleTier: string }) => rp.roleTier === 'pm').map((rp: { tsdNo: number }) => rp.tsdNo)
                ));
            }
        })();
        loadPeriodOptions('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const managerOptions: IManagerOptionType[] = managers.map((m) => ({
        value: m.tsdNo,
        label: `(${m.tsdNo}) ${m.firstName} ${m.lastName}`,
        tsdNo: m.tsdNo,
        firstName: m.firstName,
        lastName: m.lastName,
    }));

    const ownManagerLabel = (() => {
        const match = managers.find((m) => m.tsdNo === ownMgrNo);
        return match ? `(${match.tsdNo}) ${match.firstName} ${match.lastName}` : ownMgrNo ? `(${ownMgrNo})` : '';
    })();

    // VP/Area Manager/Project Manager badge for whichever manager is currently in the field -
    // independent of the VP/AM tailored-experience work (still deferred); this is just a visual
    // signal. Checked in tree order (a person should only hold one tier).
    const managerTierBadge = (() => {
        if (!selectedMgrNo) {
            return undefined;
        }
        if (vpTsdNos.has(selectedMgrNo)) {
            return 'VP';
        }
        if (areaManagerTsdNos.has(selectedMgrNo)) {
            return 'Area Manager';
        }
        if (projectManagerTsdNos.has(selectedMgrNo)) {
            return 'Project Manager';
        }
        return undefined;
    })();

    const managerTierBadgeColor = managerTierBadge === 'VP' ? '#6393C1' : managerTierBadge === 'Area Manager' ? '#8ea05c' : '#b98a3d';

    const managerLabelWithBadge = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem' }}>
            Manager
            {!!managerTierBadge && (
                <span
                    style={{
                        whiteSpace: 'nowrap', fontSize: '1rem', fontWeight: 'bold', color: '#fff',
                        backgroundColor: managerTierBadgeColor, borderRadius: '999px', padding: '.3rem 1rem',
                    }}
                >
                    {managerTierBadge}
                </span>
            )}
        </span>
    );

    const toNumber = (numStr: string) => (!!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined);

    const buildCriteria = (): ICommissionSearchCriteria | undefined => {
        const values = getValues();
        if (!selectedPeriodOption?.value) {
            setMessage('A pay period number must be entered.');
            return undefined;
        }
        setMessage('');
        return {
            periodNo: selectedPeriodOption.value,
            commissionType: values.commissionType || 'S',
            mgrNo: selectedMgrNo,
            clNo: toNumber(values.clNoSearch),
            prNo: toNumber(values.prNoSearch),
            tsdNo: toNumber(values.tsdNoSearch),
        };
    };

    const executeSearch = () => {
        const criteria = buildCriteria();
        if (criteria) {
            props.onSearch(criteria);
            setIsAdvancedOpen(false);
        }
    };

    const withCriteria = (handler: (criteria: ICommissionSearchCriteria) => void) => () => {
        const criteria = buildCriteria();
        if (criteria) {
            handler(criteria);
        }
    };

    // Bonus History (unlike every other button here) is manager-scoped, not period-scoped - it
    // has its own Payroll Period grid inside the window instead of reading :control.period_no, so
    // this skips buildCriteria's periodNo requirement and only needs a manager selected.
    const withManagerOnly = (handler: (criteria: ICommissionSearchCriteria) => void) => () => {
        if (!selectedMgrNo) {
            setMessage('Please select a manager first.');
            return;
        }
        setMessage('');
        const values = getValues();
        handler({
            periodNo: selectedPeriodOption?.value ?? 0,
            commissionType: values.commissionType || 'S',
            mgrNo: selectedMgrNo,
        });
    };

    const clearAdvancedFilters = () => {
        ADVANCED_FIELD_NAMES.forEach((field) => setValue(field, ''));
    };

    const fieldStyle = { width: '100%' };

    const renderAdvancedField = (label: string, name: string, width = '20%') => (
        <div style={{ width, paddingLeft: '4px' }}>
            <Fieldset label={label} style={{ border: 0, padding: 0 }}>
                <RHFInputBox name={name} type="number" register={register} errors={errors} style={fieldStyle} bodyStyle={fieldStyle} />
            </Fieldset>
        </div>
    );

    const isAdvancedVisible = isAdvancedOpen && !isCollapsed;

    return (
        <div style={{ position: 'relative' }}>
            {isCollapsed ? (
                <div style={{ textAlign: 'center' }}>
                    <Button
                        text="(+) Search Filters"
                        className="blue-hover-button"
                        style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                        onClick={() => setIsCollapsed(false)}
                    />
                </div>
            ) : (
                // Wraps just the main bar (not the button-bar panel below it) in its own
                // position:relative context, so the floating Advanced Search panel's top:100%
                // anchors directly under this bar - previously it anchored to the whole component
                // (main bar + button bar combined height), rendering well below both.
                <div style={{ position: 'relative' }}>
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={12}>
                        {/* Sized to its own content (fields + buttons), not stretched to match the
                            wider grid panels below - collapses the empty space that a full-width
                            row + marginLeft:'auto' buttons would otherwise leave. */}
                        <div style={{ width: 'fit-content', margin: '0 auto' }}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel>
                                    <form>
                                        <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                            <div style={{ width: '14rem', flexShrink: 0 }}>
                                                <Fieldset label="Period Number" style={{ border: 0, padding: 0 }}>
                                                    <Select
                                                        name="periodNo"
                                                        placeholder="Period#"
                                                        options={periodOptions}
                                                        value={selectedPeriodOption}
                                                        onChange={(option: IPeriodOptionType | null) => setSelectedPeriodOption(option)}
                                                        onInputChange={(inputText: string) => periodSearch(inputText)}
                                                        filterOption={(option, inputValue) => (
                                                            normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                                                        )}
                                                        onMenuOpen={() => setIsSearchingPeriods(true)}
                                                        onMenuClose={() => setIsSearchingPeriods(false)}
                                                        onBlur={() => setIsSearchingPeriods(false)}
                                                        menuIsOpen={isSearchingPeriods}
                                                        isClearable={true}
                                                        isSearchable={true}
                                                        styles={compactSelectStyles}
                                                    />
                                                </Fieldset>
                                            </div>
                                            <div style={{ width: '12rem', paddingLeft: '.5rem', flexShrink: 0 }}>
                                                <Fieldset label="Commission Type" style={{ border: 0, padding: 0 }} labelStyle={{ whiteSpace: 'nowrap' }}>
                                                    <RHFSelect
                                                        name="commissionType"
                                                        defaultVal="S"
                                                        options={COMMISSION_TYPE_OPTIONS}
                                                        register={register}
                                                        style={fieldStyle}
                                                        bodyStyle={fieldStyle}
                                                    />
                                                </Fieldset>
                                            </div>
                                            <div style={{ width: '20rem', paddingLeft: '.5rem', flexShrink: 0 }}>
                                                <Fieldset label={managerLabelWithBadge} style={{ border: 0, padding: 0 }}>
                                                    {canSearchAnyManager ? (
                                                        <Select
                                                            name="mgrNoSearch"
                                                            placeholder="(Number) Name"
                                                            options={managerOptions}
                                                            value={managerOptions.find((o) => o.value === selectedMgrNo) ?? null}
                                                            onChange={(option: IManagerOptionType | null) => setSelectedMgrNo(option?.value)}
                                                            isClearable={true}
                                                            isSearchable={true}
                                                            styles={compactSelectStyles}
                                                        />
                                                    ) : (
                                                        <RHFInputBox
                                                            key={ownManagerLabel}
                                                            name="mgrNoDisplay"
                                                            type="text"
                                                            readOnly={true}
                                                            defaultVal={ownManagerLabel}
                                                            style={fieldStyle}
                                                            bodyStyle={fieldStyle}
                                                        />
                                                    )}
                                                </Fieldset>
                                            </div>
                                            <div style={{ display: 'flex', flexShrink: 0, paddingLeft: '1rem' }}>
                                                <Button
                                                    text="Search"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => executeSearch()}
                                                />
                                                <Button
                                                    text="(-) Minimize"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                    onClick={() => setIsCollapsed(true)}
                                                />
                                                <Button
                                                    text="Advanced Search"
                                                    reactIcon={FaChevronDown}
                                                    iconStyle={{
                                                        fontSize: '1.6rem',
                                                        marginRight: '.5rem',
                                                        transform: `scaleY(${isAdvancedOpen ? -1 : 1})`,
                                                        transition: 'transform .25s ease',
                                                        verticalAlign: 'middle',
                                                    }}
                                                    className="blue-hover-button"
                                                    style={{
                                                        whiteSpace: 'nowrap', marginLeft: '1rem', width: '14rem', height: '3rem',
                                                        verticalAlign: 'middle', borderRadius: '5px',
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    }}
                                                    onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                                />
                                            </div>
                                        </div>
                                        {!!message && (
                                            <div style={{ color: 'red', textAlign: 'center', paddingBottom: '.5rem' }}>{message}</div>
                                        )}
                                    </form>
                                </Panel>
                            </Panel>
                        </div>
                    </Page.Section>
                </Page>
                {/* Floating "Advanced Search" panel - anchored to just the main bar above (see the
                    wrapping position:relative div this was moved into), not the whole component,
                    so it no longer renders below the button-bar panel too. Uses a translateY +
                    opacity slide (not max-height, which barely animates - transitioning max-height
                    from 0 to a value far larger than the actual content snaps open almost
                    instantly) for a real, visible slide-down. */}
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 40,
                        transform: isAdvancedVisible ? 'translateY(0)' : 'translateY(-16px)',
                        opacity: isAdvancedVisible ? 1 : 0,
                        pointerEvents: isAdvancedVisible ? 'auto' : 'none',
                        transition: 'transform .3s ease, opacity .3s ease',
                        boxShadow: '0 8px 16px rgba(0,0,0,.3)',
                    }}
                >
                    <Page style={{ justifyContent: 'center' }}>
                        <Page.Section width={12}>
                            <div style={{ maxWidth: '130rem', margin: '0 auto' }}>
                                <Panel backgroundColor="#D3D3D3">
                                    <Panel panelTitle="Advanced Search">
                                        <form>
                                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                {renderAdvancedField('Client No.', 'clNoSearch')}
                                                {renderAdvancedField('Project No.', 'prNoSearch')}
                                                {renderAdvancedField('Landman No.', 'tsdNoSearch')}
                                            </div>
                                            <div className="form-row" style={{ padding: '.5rem', margin: '0', justifyContent: 'center', gap: '1rem' }}>
                                                <Button text="Search" className="blue-hover-button" style={{ width: '10rem', height: '3rem', borderRadius: '5px' }} onClick={() => executeSearch()} />
                                                <Button
                                                    text="Clear"
                                                    className="blue-hover-button"
                                                    style={{ width: '10rem', height: '3rem', borderRadius: '5px' }}
                                                    onClick={() => clearAdvancedFilters()}
                                                />
                                            </div>
                                        </form>
                                    </Panel>
                                </Panel>
                            </div>
                        </Page.Section>
                    </Page>
                </div>
                </div>
            )}

            {!isCollapsed && (
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={12}>
                        {/* Sized to just the buttons it holds, not the full page width. */}
                        <div style={{ width: 'fit-content', margin: '.5rem auto 0' }}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', padding: '.5rem' }}>
                                        {canSearchAnyManager ? (
                                            <>
                                                <Button
                                                    text="Build Manager Review"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                                    onClick={withCriteria(props.onBuildManagerReview)}
                                                />
                                                <Button
                                                    text="Build Single Manager Review"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                                    onClick={withCriteria(props.onBuildSingleManagerReview)}
                                                />
                                                {/* Mirrors Control.B_Add_Period_No's sibling - "Multiple Periods" (oid 46,
                                                    Go_Block('PP_Range')) - admin-only, same as the two build buttons above,
                                                    since it can build commissions for every manager across a whole period range. */}
                                                <Button
                                                    text="Multiple Periods"
                                                    className="blue-hover-button"
                                                    style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                                    onClick={() => props.onMultiplePeriods()}
                                                />
                                            </>
                                        ) : (
                                            // Mirrors manager_commission.fmt's Control.B_Add_Period_No - the manager-scope
                                            // stand-in for the two admin-only build buttons above, neither of which is
                                            // appropriate for a self-service manager (one rebuilds every manager company-wide,
                                            // the other lets you type in any arbitrary manager number).
                                            <Button
                                                text="Add Period"
                                                className="blue-hover-button"
                                                style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                                onClick={withCriteria(props.onAddPeriod)}
                                            />
                                        )}
                                        <Button
                                            text="Reports"
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                            onClick={withCriteria(props.onReports)}
                                        />
                                        <Button
                                            text="Summary Report"
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                            onClick={withCriteria(props.onSummaryReport)}
                                        />
                                        <Button
                                            text="Excel Report"
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                            onClick={withCriteria(props.onExcelReport)}
                                        />
                                        {/* Mirrors manager_commission.fmt's "PC/ASM Totals" button (B_PCASM) - available in
                                            both modes, matching Oracle's own (reads whichever manager is in the field
                                            regardless of who's logged in). */}
                                        <Button
                                            text="PC/ASM Totals"
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                            onClick={withCriteria(props.onPcAsmTotals)}
                                        />
                                        {/* Mirrors manager_commission.fmt's "Bonus History" button (B_PB_HISTORY) - opens the
                                            Production Bonus History window (canvas HIST_CONTROL), same both-modes
                                            availability as PC/ASM Totals above. */}
                                        <Button
                                            text="Bonus History"
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', height: '3rem', borderRadius: '5px' }}
                                            onClick={withManagerOnly(props.onBonusHistory)}
                                        />
                                    </div>
                                </Panel>
                            </Panel>
                        </div>
                    </Page.Section>
                </Page>
            )}
        </div>
    );
};

export default CommissionSearch;



// WEBPACK FOOTER //
// ./src/components/personnel/components/CommissionSearch.tsx